import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { format } from 'd3-format';
import { Link } from 'found';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import {
  Button, Card, CardBody, CardFooter, CardSubtitle, CardText, CardTitle, Col, Row,
} from 'reactstrap';
import styled from 'styled-components';

import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import { APIConfig } from 'src/config';
import PrioritizeNominatedTradesMutation from 'src/mutations/PrioritizeNominatedTradesMutation';
import FlashesStore from 'src/stores/FlashesStore';
import { PLATFORM_MODE_REBATE, TRADE_DIRECTION_BUY } from 'src/util/constants';
import { convertEnergyPricePowertracerToPartner } from 'src/util/conversions';
import { useIntlCurrency } from 'src/util/i18n/currency';
import { unitOfMeasure } from 'src/util/i18n/energyPrice';
import { buildTradeRuleMeterLabel } from 'src/util/i18n/trade';
import { buildActorTimestampMessage } from 'src/util/i18n/tradeRule';
import { getPriceRange } from 'src/util/tradeRule';

const messages = defineMessages({
  proposal: {
    id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.proposal.details',
    defaultMessage: 'Proposed by {actor} on {timestamp}',
  },
  proposalNoActor: {
    id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.proposal.details.no_actor',
    defaultMessage: 'Proposed on {timestamp}',
  },
  accepted: {
    id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.accepted.details',
    defaultMessage: 'Accepted by {actor} on {timestamp}',
  },
  acceptedNoActor: {
    id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.accepted.details.no_actor',
    defaultMessage: 'Accepted on {timestamp}',
  },
});

const DraggableCard = styled.div.attrs({
  className: 'card mb-4',
})`
  box-sizing: content-box;
  padding-right: 3rem;
`;

const DraggableCardHandle = styled.div`
  position: absolute;
  right: 0;
  vertical-align: middle;
  width: 3rem;
  background: #eee;
  height: 100%;
`;

const DraggableCardHandleIcon = styled.div`
  width: 100%;
  margin-top: 5rem;

  &:before {
    content: '.';
    font-size: 1rem;
    line-height: 1rem;
    text-shadow: 0 5px #666, 0 10px #666, 5px 0 #666, 5px 5px #666, 5px 10px #666, 10px 0 #666, 10px 5px #666, 10px 10px #666;
    color: #666;
    padding-left: 1rem;
    text-align: center;
  }
`;

const MUTATION_NAME = 'prioritizeNominatedTrades';

const TradeRulePrioritizeNominatedListPropertyFragment = graphql`
  fragment TradeRulePrioritizeNominatedList_property on Property @throwOnFieldError {
    uuid
  }
`;

const TradeRulePrioritizeNominatedListMeterFragment = graphql`
  fragment TradeRulePrioritizeNominatedList_meter on Meter @throwOnFieldError {
    uuid
    identifier
    title
    tradePointId
    nominatedRules: rules(first: 500, type: TRADE_TYPE_NOMINATED, state: TRADE_RULE_STATE_ACCEPTED) {
      edges {
        node {
          uuid
          priority
          tradeType
          state
          buyer {
            userId
            communityId
            residualId
            tradePoint {
              uuid
              meter {
                uuid
                identifier
                title
                property {
                  uuid
                  title
                }
              }
            }
          }
          seller {
            userId
            communityId
            residualId
            tradePoint {
              uuid
              meter {
                uuid
                identifier
                title
                property {
                  uuid
                  title
                }
              }
            }
          }
          clauses {
            edges {
              node {
                price
              }
            }
          }
          start
          finish
          proposedAt
          proposedBy {
            uuid
            email
            givenName
            familyName
          }
          proposedById
          acceptedAt
          acceptedBy {
            uuid
            email
            givenName
            familyName
          }
          acceptedById
        }
      }
    }
  }
`;

/**
 * Derives the buyer-side nominated rules for a meter, keyed by rule uuid.
 * @param {object|null} meter - Resolved `TradeRulePrioritizeNominatedList_meter` data.
 * @returns {object} Map of rule uuid to rule node.
 */
const buildRulesByUuid = (meter) => {
  const rules = {};
  if (meter && meter.nominatedRules && meter.nominatedRules.edges) {
    meter.nominatedRules.edges.forEach((edge) => {
      const { node } = edge;
      if (node.buyer.tradePoint.uuid === meter.tradePointId) {
        rules[node.uuid] = node;
      }
    });
  }
  return rules;
};

/**
 * @param {object} props
 * @param {object} props.property
 * @param {object} props.meter
 * @param {object} props.router
 * @returns {React.ReactComponentElement} - TradeRulePrioritizeNominatedList component
 */
function TradeRulePrioritizeNominatedList({
  property: propertyRef = null, meter: meterRef = null, router,
}) {
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  const property = useFragment(TradeRulePrioritizeNominatedListPropertyFragment, propertyRef);
  const meter = useFragment(TradeRulePrioritizeNominatedListMeterFragment, meterRef);

  const rules = useMemo(() => buildRulesByUuid(meter), [meter]);
  const sortedRuleIds = useMemo(
    () => Object.values(rules).sort((a, b) => a.priority - b.priority).map((rule) => rule.uuid),
    [rules],
  );

  const [ruleIds, setRuleIds] = useState(sortedRuleIds);
  const [processing, setProcessing] = useState(false);
  // Re-derives ruleIds whenever the resolved meter changes (e.g. a route retry
  // resolves a fresh payload), while still allowing onDragEnd to reorder
  // ruleIds locally between renders. Comparing against the previous
  // sortedRuleIds and calling setRuleIds during render is the pattern React
  // recommends for resetting state when a prop changes:
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [previousSortedRuleIds, setPreviousSortedRuleIds] = useState(sortedRuleIds);
  if (sortedRuleIds !== previousSortedRuleIds) {
    setPreviousSortedRuleIds(sortedRuleIds);
    setRuleIds(sortedRuleIds);
  }

  if (!property || !meter) {
    return <Loading />;
  }

  const handleConfirmFailure = (error) => {
    setProcessing(false);
    FlashesStore.flash(FlashesStore.ERROR, error, MUTATION_NAME);
  };

  const handleConfirmSuccess = (_response) => {
    const messageSuccess = intl.formatMessage({ id: 'trade_rule.trade_rule_prioritize_nominated.form.response_message.success', defaultMessage: 'Updated peer-to-peer trades priority order.' });

    setProcessing(false);
    FlashesStore.flash(FlashesStore.SUCCESS, messageSuccess, MUTATION_NAME);
    router.push(`/properties/${property.uuid}/trade-rules/active`);
  };

  const handleConfirm = () => {
    if (processing) {
      return;
    }

    setProcessing(true);
    FlashesStore.reset();

    PrioritizeNominatedTradesMutation(
      meter.tradePointId,
      ruleIds.map((id, index) => ({ id, priority: index + 1 })),
      handleConfirmSuccess,
      handleConfirmFailure,
    );
  };

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) {
      return;
    }

    const newRuleIds = [...ruleIds];
    newRuleIds.splice(source.index, 1);
    newRuleIds.splice(destination.index, 0, draggableId);

    setRuleIds(newRuleIds);
  };

  const tradeRules = ruleIds.map((ruleId) => rules[ruleId]);
  const copyTitle = intl.formatMessage({ id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.copy_title', defaultMessage: 'Copy trade rule id to the clipboard' });
  const copySuccessMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.copy.success.message', defaultMessage: 'Trade rule id copied to the clipboard.' });
  const tradeRuleIdLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.id.label', defaultMessage: 'Trade Rule ID:' });
  const furtherDetailsLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.further_details.text', defaultMessage: 'further details' });
  const headerLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.header.text', defaultMessage: 'Peer-to-Peer Trades for {meterTitle}' }, { meterTitle: meter?.title });
  const changePriorityLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.change_priority.text', defaultMessage: 'Change buying priority order' });
  const reorderLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.reorder.text', defaultMessage: 'Reorder the priority of your {tradeRules} peer-to-peer trade rules to buy energy.' }, { tradeRules: <strong>{tradeRules.length}</strong> });
  const approvedLabel = intl.formatMessage({ id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.approved.label', defaultMessage: 'Approved' });
  const confirmOrderLabel = intl.formatMessage({ id: 'trade_rule_priority.confirm_order.label', defaultMessage: 'Confirm order' });
  const cancelChangesLabel = intl.formatMessage({ id: 'trade_rule_priority.cancel_changes.label', defaultMessage: 'Cancel changes' });

  const energyPricePerUnit = unitOfMeasure(intl);

  return (
    <Card className="mt-4 mb-4 trade-rules-priority-list">
      <CardBody>
        <h2>
          {headerLabel}
        </h2>
        <Row>
          <Col xs="12" sm="12">
            <h3>{changePriorityLabel}</h3>
            <p data-testid="subHeading">
              {reorderLabel}
            </p>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable">
                {((droppableProvided) => (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                    {tradeRules.map((rule, index) => {
                      const {
                        acceptedAt, acceptedBy, acceptedById, buyer, uuid,
                        priority, proposedAt, proposedBy, proposedById, seller,
                      } = rule;
                      const newPriority = index + 1;

                      const priceRange = getPriceRange(rule);
                      const furtherDetails = <Link to={`/properties/${property.uuid}/trade-rules/${uuid}`}>{furtherDetailsLabel}</Link>;

                      let priceLabel;
                      if (APIConfig().MODE === PLATFORM_MODE_REBATE) {
                        if (priceRange.maximum === priceRange.minimum) {
                          priceLabel = intl.formatMessage({
                            id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.mode_rebate.sell_price.flat',
                            defaultMessage: 'Discount of {price} {energyPricePerUnit} ({furtherDetails})',
                          }, {
                            price: <strong>{format('.3f')(convertEnergyPricePowertracerToPartner(intlCurrency, priceRange.minimum))}</strong>,
                            energyPricePerUnit,
                            furtherDetails,
                          });
                        }
                        if (priceRange.maximum !== priceRange.minimum) {
                          priceLabel = intl.formatMessage({
                            id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.mode_rebate.sell_price.variable',
                            defaultMessage: 'Discount of between {minPrice} and {maxPrice} {energyPricePerUnit} ({furtherDetails})',
                          }, {
                            minPrice: <strong>{format('.3f')(convertEnergyPricePowertracerToPartner(intlCurrency, priceRange.minimum))}</strong>,
                            maxPrice: <strong>{format('.3f')(convertEnergyPricePowertracerToPartner(intlCurrency, priceRange.maximum))}</strong>,
                            energyPricePerUnit,
                            furtherDetails,
                          });
                        }
                      } else {
                        if (priceRange.maximum === priceRange.minimum) {
                          priceLabel = intl.formatMessage({
                            id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.mode_trade.sell_price.flat',
                            defaultMessage: 'Sell price of {price} {energyPricePerUnit} ({furtherDetails})',
                          }, {
                            price: <strong>{format('.3f')(convertEnergyPricePowertracerToPartner(intlCurrency, priceRange.minimum))}</strong>,
                            energyPricePerUnit,
                            furtherDetails,
                          });
                        }
                        if (priceRange.maximum !== priceRange.minimum) {
                          priceLabel = intl.formatMessage({
                            id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.mode_trade.sell_price.variable',
                            defaultMessage: 'Sell price of between {minPrice} and {maxPrice} {energyPricePerUnit} ({furtherDetails})',
                          }, {
                            minPrice: <strong>{format('.3f')(convertEnergyPricePowertracerToPartner(intlCurrency, priceRange.minimum))}</strong>,
                            maxPrice: <strong>{format('.3f')(convertEnergyPricePowertracerToPartner(intlCurrency, priceRange.maximum))}</strong>,
                            energyPricePerUnit,
                            furtherDetails,
                          });
                        }
                      }

                      return (
                        <Draggable draggableId={uuid} index={index} key={`trade-rules-${uuid}`}>
                          {(draggableProvided) => (
                            <DraggableCard
                              ref={draggableProvided.innerRef}
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              {...draggableProvided.draggableProps}
                            >
                              <CardBody>
                                <CardTitle tag="h4">
                                  {intl.formatMessage({
                                    id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.priority.label',
                                    defaultMessage: 'Priority {priority} {previousPriority}',
                                  }, {
                                    priority: newPriority,
                                    previousPriority: newPriority !== priority && (
                                      <s>{`(${priority})`}</s>
                                    ),
                                  })}
                                </CardTitle>
                                <CardSubtitle tag="h5" className="mb-2">
                                  {approvedLabel}
                                </CardSubtitle>
                                <CardText>
                                  {intl.formatMessage({
                                    id: 'trade_rule.trade_rule_prioritize_nominated.trade_rule_prioritize_nominated_list.trade_direction_buy.details',
                                    defaultMessage: 'Buying for {meter}',
                                  }, {
                                    meter: buildTradeRuleMeterLabel(
                                      intl,
                                      buyer,
                                      seller,
                                      TRADE_DIRECTION_BUY,
                                      false,
                                    ),
                                  })}
                                  <br />
                                  {buildActorTimestampMessage(intl, {
                                    user: proposedBy,
                                    userId: proposedById,
                                    timestamp: proposedAt,
                                    descriptor: messages.proposal,
                                    fallbackDescriptor: messages.proposalNoActor,
                                  })}
                                  <br />
                                  {acceptedAt && buildActorTimestampMessage(intl, {
                                    user: acceptedBy,
                                    userId: acceptedById,
                                    timestamp: acceptedAt,
                                    descriptor: messages.accepted,
                                    fallbackDescriptor: messages.acceptedNoActor,
                                  })}
                                  <br />
                                  {priceLabel}
                                  <br />
                                  <>
                                    <span className="me-1">{tradeRuleIdLabel}</span>
                                    <UUID
                                      copyTitle={copyTitle}
                                      copySuccessMsg={copySuccessMsg}
                                      uuid={uuid}
                                      url={`/properties/${property.uuid}/trade-rules/${uuid}`}
                                    />
                                  </>
                                </CardText>
                              </CardBody>

                              {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                              <DraggableCardHandle {...draggableProvided.dragHandleProps} className="trade-rules-priority-handler">
                                <DraggableCardHandleIcon />
                              </DraggableCardHandle>
                            </DraggableCard>
                          )}
                        </Draggable>
                      );
                    })}
                    {droppableProvided.placeholder}
                  </div>
                ))}
              </Droppable>
            </DragDropContext>
          </Col>
        </Row>
      </CardBody>
      <CardFooter>
        <Button color="primary" className="me-2" onClick={handleConfirm} disabled={processing}>
          {confirmOrderLabel}
        </Button>
        <Button color="" onClick={() => (router.go(-1))} disabled={processing}>
          {cancelChangesLabel}
        </Button>
      </CardFooter>
    </Card>
  );
}

TradeRulePrioritizeNominatedList.propTypes = {
  meter: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  property: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  router: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default TradeRulePrioritizeNominatedList;
